import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import { Radar, RadarChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Sparkles, CalendarDays, Trophy, ArrowRight } from "lucide-react";
import Markdown from "../../components/Markdown";
import VideoLibraryCard from "../../components/VideoLibraryCard";
import { toast } from "sonner";

const SKILLS = ["forehand", "backhand", "serve", "dink", "volley", "strategy"];

export default function StudentDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.user_id) return;
    api.get(`/analytics/student/${user.user_id}`).then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/lessons").then(({ data }) => setLessons(data || [])).catch(() => {});
    api.get(`/progress/student/${user.user_id}`).then(({ data }) => setProgress(data || [])).catch(() => {});
  }, [user?.user_id]);

  const radarData = useMemo(() => {
    const latest = analytics?.latest_ratings || {};
    return SKILLS.map((k) => ({ skill: k.toUpperCase(), v: Number(latest[k] || 0) }));
  }, [analytics]);

  const upcoming = (lessons || []).filter((l) => l.status === "scheduled").slice(0, 4);

  const getSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/progress-summary/${user.user_id}`);
      setSummary(data.summary);
    } catch (_) { toast.error("AI failed"); }
    setLoading(false);
  };

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      <div className="grid md:grid-cols-4 gap-4">
        <StatTile label="Completed" value={analytics?.lessons_completed ?? "—"} testId="stat-completed" icon={CalendarDays} />
        <StatTile label="Upcoming" value={analytics?.lessons_upcoming ?? "—"} testId="stat-upcoming" icon={CalendarDays} />
        <StatTile label="Milestones" value={(analytics?.milestones || []).length} testId="stat-milestones" icon={Trophy} />
        <StatTile label="DUPR" value={user?.skill_level ?? "—"} testId="stat-dupr" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-surface p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-4">Skill Radar</div>
          <div className="h-72">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="skill" stroke="#A3A3A3" tick={{ fontSize: 10 }} />
                <PolarRadiusAxis stroke="#A3A3A3" domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Radar dataKey="v" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">AI Coach Report</div>
            <button onClick={getSummary} disabled={loading} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="summary-generate-button">
              <Sparkles size={14} /> {loading ? "…" : "Generate"}
            </button>
          </div>
          <div className="mt-4 text-sm">
            {summary ? <Markdown>{summary}</Markdown> : <div className="text-[#A3A3A3] text-sm">Tap generate for a personalized AI summary of your progress.</div>}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Up next</div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">Your schedule</h3>
          </div>
          <Link to="/student/book" className="text-xs uppercase tracking-widest font-bold text-[#38BDF8] hover:underline" data-testid="book-new-link">
            Book new →
          </Link>
        </div>
        {upcoming.length === 0 ? (
          <div className="border border-dashed border-white/10 p-8 text-[#A3A3A3]">No upcoming lessons. Hit Book new to find a slot.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {upcoming.map((l) => (
              <Link key={l.id} to={`/student/lessons/${l.id}`} className="card-surface p-6" data-testid={`upcoming-${l.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">
                  {new Date(l.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="font-display text-2xl font-black uppercase tracking-tight mt-2">{l.title}</div>
                <div className="text-xs text-[#A3A3A3] mt-2">with {l.coach_name} · {l.duration_mins}min</div>
                <div className="text-xs text-white mt-4 inline-flex items-center gap-1">Open <ArrowRight size={12} /></div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {(analytics?.milestones || []).length > 0 && (
        <div className="card-surface p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Milestones</div>
          <div className="mt-4 space-y-3">
            {analytics.milestones.map((m) => (
              <div key={m.id} className="flex items-start gap-3 border-l-2 border-[#38BDF8] pl-4" data-testid={`milestone-${m.id}`}>
                <Trophy size={16} className="text-[#38BDF8] mt-1" />
                <div>
                  <div className="font-bold">{m.milestone}</div>
                  <div className="text-xs text-[#A3A3A3]">{new Date(m.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {user?.user_id && (
        <VideoLibraryCard studentId={user.user_id} role="student" />
      )}

      {progress.length > 0 && (
        <div className="card-surface p-6" data-testid="student-progress-history">
          <div className="flex items-end justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Progress log ({progress.length})</div>
          </div>
          <div className="mt-4 space-y-5">
            {progress.slice().reverse().slice(0, 8).map((p) => (
              <div key={p.id} className="border-l-2 border-white/10 pl-4 py-1" data-testid={`student-progress-${p.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
                  {new Date(p.created_at).toLocaleString()}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SKILLS.map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-widest border border-white/10 px-2 py-1">
                      {s} <span className="text-[#38BDF8] font-bold ml-1">{Number((p.skill_ratings || {})[s] || 0)}</span>
                    </span>
                  ))}
                </div>
                {p.areas_to_improve && (
                  <div className="mt-2 text-xs"><span className="text-[#A3A3A3] uppercase tracking-widest">Work on:</span> {p.areas_to_improve}</div>
                )}
                {p.notes && (
                  <div className="mt-1 text-xs text-white/80 whitespace-pre-wrap"><span className="text-[#A3A3A3] uppercase tracking-widest">Coach notes:</span> {p.notes}</div>
                )}
                {p.insights && (
                  <div className="mt-2 text-xs text-[#38BDF8] whitespace-pre-wrap" data-testid={`progress-insight-${p.id}`}>
                    <span className="text-[#A3A3A3] uppercase tracking-widest">Coach insight:</span> {p.insights}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: I, testId }) {
  return (
    <div className="card-surface p-6" data-testid={testId}>
      {I && <I size={18} className="text-[#38BDF8]" />}
      <div className="font-display text-5xl font-black mt-4">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-2">{label}</div>
    </div>
  );
}
